/**
 * Embed Video Player Custom Element
 * Handles Shopify, YouTube, and Vimeo videos with unified controls
 */
class EmbedVideoPlayer extends HTMLElement {
  constructor() {
    super();
    this.videoContainer = null;
    this.videoType = null;
    this.videoElement = null;
    this.iframeElement = null;
    this.scrollObserver = null;
    this.pauseOnScroll = true;
  }

  connectedCallback() {
    this.videoContainer = this.querySelector('[data-video-container]');
    this.videoType = this.dataset.videoType;
    this.pauseOnScroll = this.dataset.pauseOnScroll !== 'false';

    if (!this.videoContainer) return;

    // Get video or iframe element
    this.videoElement = this.videoContainer.querySelector('video');
    this.iframeElement = this.videoContainer.querySelector('iframe');

    this.initializeControls();
    this.initializeScrollPause();
  }

  disconnectedCallback() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  }

  /**
   * Initialize click controls for Shopify videos
   */
  initializeControls() {
    if (this.videoType === 'shopify' && this.videoElement && this.videoContainer) {
      // Click to play/pause for Shopify videos
      this.videoContainer.addEventListener('click', (e) => {
        // Don't interfere with native controls
        if (e.target && e.target instanceof HTMLElement && e.target.tagName === 'VIDEO') {
          this.togglePlayPause();
        }
      });

      // Keyboard support
      this.videoElement.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.togglePlayPause();
        }
      });
    }
  }

  /**
   * Initialize IntersectionObserver to pause video when scrolled out of view
   */
  initializeScrollPause() {
    if (!this.pauseOnScroll) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            // Video scrolled out of view - pause it
            this.pauseVideo();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.3 // Trigger when 30% of video is visible
      }
    );

    this.scrollObserver.observe(this.videoContainer);
  }

  /**
   * Play the current video
   */
  playVideo() {
    if (this.videoType === 'shopify' && this.videoElement) {
      // Shopify video - direct control
      this.videoElement.play().catch((error) => {
        console.warn('Video play failed:', error);
      });
    } else if (this.videoType === 'youtube' && this.iframeElement) {
      // YouTube - use postMessage API
      this.iframeElement.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'playVideo',
          args: ''
        }),
        '*'
      );
    } else if (this.videoType === 'vimeo' && this.iframeElement) {
      // Vimeo - use postMessage API
      this.iframeElement.contentWindow.postMessage(
        JSON.stringify({
          method: 'play'
        }),
        '*'
      );
    }
  }

  /**
   * Pause the current video
   */
  pauseVideo() {
    if (this.videoType === 'shopify' && this.videoElement) {
      // Shopify video - direct control
      if (!this.videoElement.paused) {
        this.videoElement.pause();
      }
    } else if (this.videoType === 'youtube' && this.iframeElement) {
      // YouTube - use postMessage API
      this.iframeElement.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'pauseVideo',
          args: ''
        }),
        '*'
      );
    } else if (this.videoType === 'vimeo' && this.iframeElement) {
      // Vimeo - use postMessage API
      this.iframeElement.contentWindow.postMessage(
        JSON.stringify({
          method: 'pause'
        }),
        '*'
      );
    }
  }

  /**
   * Toggle play/pause state
   */
  togglePlayPause() {
    if (this.videoType === 'shopify' && this.videoElement) {
      if (this.videoElement.paused) {
        this.playVideo();
      } else {
        this.pauseVideo();
      }
    } else {
      // For iframes, we can't easily check state, so just send play command
      this.playVideo();
    }
  }

  /**
   * Check if video is currently playing
   * @returns {boolean}
   */
  isPlaying() {
    if (this.videoType === 'shopify' && this.videoElement) {
      return !this.videoElement.paused;
    }
    // For iframes, we can't reliably check without complex state management
    return false;
  }

  /**
   * Get current video time (Shopify only)
   * @returns {number}
   */
  getCurrentTime() {
    if (this.videoType === 'shopify' && this.videoElement) {
      return this.videoElement.currentTime;
    }
    return 0;
  }

  /**
   * Set video time (Shopify only)
   * @param {number} time - Time in seconds
   */
  setCurrentTime(time) {
    if (this.videoType === 'shopify' && this.videoElement) {
      this.videoElement.currentTime = time;
    }
  }

  /**
   * Get video duration (Shopify only)
   * @returns {number}
   */
  getDuration() {
    if (this.videoType === 'shopify' && this.videoElement) {
      return this.videoElement.duration;
    }
    return 0;
  }
}

// Register the custom element
if (!customElements.get('embed-video-player')) {
  customElements.define('embed-video-player', EmbedVideoPlayer);
}

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = EmbedVideoPlayer;
}
